import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { X, AlertTriangle, Clock } from 'lucide-react';
import { grantGrace, getSubscriptionStatus } from '../../redux/subscriptionSlice';

// Admin: grant an EXPIRED doctor N days of temporary access (e.g. to finish a
// booked appointment). Opened from the red expired flag on the Doctors table.
const GraceModal = ({ doctor, onClose, onGranted }) => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState(null);
  const [days, setDays] = useState('');
  const [granting, setGranting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const res = await dispatch(getSubscriptionStatus(doctor.doctorId));
      if (active) setStatus(res.payload || null);
    })();
    return () => { active = false; };
  }, [doctor.doctorId, dispatch]);

  const grant = async () => {
    const n = Math.floor(Number(days));
    if (!n || n <= 0) { toast.warn('Enter a number of days'); return; }
    setGranting(true);
    try {
      const res = await dispatch(grantGrace({ doctorId: doctor.doctorId, days: n })).unwrap();
      setStatus(res.status || status);
      toast.success(`Granted ${n} day${n === 1 ? '' : 's'} of access`);
      onGranted?.();
      onClose();
    } catch (e) {
      toast.warn(typeof e === 'string' ? e : 'Could not grant grace period');
    } finally {
      setGranting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] px-4" data-testid="grace-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-bold text-[#1a365d]">Grant temporary access</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-xl text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <p className="text-sm text-slate-500">
            <span className="font-semibold text-gray-700">{doctor.name || doctor.doctorId}</span>’s subscription has expired.
            Grant extra days so they can keep using the app (e.g. to manage a booked appointment).
          </p>

          {status && (
            <div className="flex items-center justify-between text-sm bg-red-50 text-red-700 rounded-xl px-3 py-2">
              <span>{status.packageName || '—'}</span>
              <span className="font-semibold">
                {status.status === 'expired' ? `Expired${status.endDate ? ` · ${new Date(status.endDate).toLocaleDateString()}` : ''}` : `${status.daysLeft ?? 0} days left`}
              </span>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-[#4ABDBD]" /> Days of access to grant
            </label>
            <input
              type="number"
              min="1"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="e.g. 7"
              data-testid="grace-days"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
            />
          </div>
        </div>

        <div className="p-4 sm:p-6 border-t border-gray-200 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm transition-colors">
            Cancel
          </button>
          <button
            type="button"
            onClick={grant}
            disabled={granting}
            data-testid="grant-grace"
            className={`px-4 py-2 rounded-xl text-white font-semibold text-sm transition-all ${granting ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:shadow-lg'}`}
          >
            {granting ? 'Granting…' : 'Grant access'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GraceModal;
